import { useState, useCallback, useEffect } from 'react';
import { entityColumnsConfig } from '@/config/entityColumns';
import type { EntityType } from '@/types/api';

const MAX_COLUMNS = 10;

function getStorageKey(entityType: EntityType, restaurantId: number | null): string {
  return `menu_columns_${entityType}_${restaurantId}`;
}

function loadSavedColumns(entityType: EntityType, restaurantId: number | null): string[] | null {
  try {
    const saved = localStorage.getItem(getStorageKey(entityType, restaurantId));
    if (saved) {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed) && parsed.length > 0 && parsed.length <= MAX_COLUMNS) {
        return parsed;
      }
    }
  } catch { /* ignore */ }
  return null;
}

export function useColumnPreferences(entityType: EntityType, restaurantId: number | null) {
  const config = entityColumnsConfig[entityType];

  const [selectedKeys, setSelectedKeys] = useState<string[]>(() => {
    return loadSavedColumns(entityType, restaurantId) || config.defaults;
  });

  // The initializer above only runs on mount; re-apply saved/default columns
  // whenever the viewed entity (or restaurant) changes.
  useEffect(() => {
    setSelectedKeys(loadSavedColumns(entityType, restaurantId) || entityColumnsConfig[entityType].defaults);
  }, [entityType, restaurantId]);

  const toggleColumn = useCallback((key: string) => {
    setSelectedKeys(prev => {
      let next: string[];
      if (prev.includes(key)) {
        if (prev.length <= 1) return prev; // Don't remove last column
        next = prev.filter(k => k !== key);
      } else {
        if (prev.length >= MAX_COLUMNS) return prev;
        next = [...prev, key];
      }
      localStorage.setItem(getStorageKey(entityType, restaurantId), JSON.stringify(next));
      return next;
    });
  }, [entityType, restaurantId]);

  const resetToDefaults = useCallback(() => {
    setSelectedKeys(config.defaults);
    localStorage.removeItem(getStorageKey(entityType, restaurantId));
  }, [entityType, restaurantId, config.defaults]);

  return {
    selectedKeys,
    setSelectedKeys,
    toggleColumn,
    resetToDefaults,
    allColumns: config.all,
    maxColumns: MAX_COLUMNS,
    isAtMax: selectedKeys.length >= MAX_COLUMNS,
  };
}
